import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { ensureBoolean, i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';

import ActionButton from '../builder/ActionButton.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import { localizeFields } from '../builder/DynamicFields.js';
import SaveButton from '../builder/SaveButton.js';

import LeveledHTMLText from './helpers/LeveledHTMLText.js';

import { getAvailableIntervals, getFilteredMatches, getFullImage, getIntervalFromResults, getTeamInfoByID, getTeamName } from './GuessScoreClient.js';
import FloatingLayerPublic from './FloatingLayerPublic.js';

import GuessScoreCanvas from '../games/GuessScoreCanvas.js';

export const TeamInfo = ({ team, ...props }) => {
	useCSS('comp/guess_score.css');

	const direction = props?.direction ?? 1;
	const img_size = 40;

	const classes = ['team_info'];
	if(direction < 0) classes.push('reversed');

	let missing_img = false;

	const name = getTeamName(team);
	let img = getFullImage(team, 'small');
	if(img === false){
		img='/img/blank_1x1.png';
		missing_img = true;
	}

	return (
		<div class={classes.join(" ")}>
			<img
				src={img}
				width={`${img_size}px`}
				height={`${img_size}px`}
				style={(missing_img) ? { backgroundColor:'rgba(var(--text-color-rgb), 1)' }: null}
				alt={name}
			/>

			<strong class="name">{name}</strong>
		</div>
	)
};

const MatchResult = forwardRef(({ user_answer, match, ...props }, ref) => {
	const [ id_1, setID1 ] = useState(parseInt(user_answer?.id_1 ?? 0));
	const [ id_2, setID2 ] = useState(parseInt(user_answer?.id_2 ?? 0));

	const winner_1 = (id_1 > id_2) ? true : false;
	const winner_2 = (id_1 < id_2) ? true : false;

	useImperativeHandle(ref, () => ({
		updateResults(results) {
			setID1(results.id_1);
			setID2(results.id_2);
		},
	}), []);

	return (
		<div class="match_result">
			<div class="c">
				<span onClick={props.onClick}>
					{(winner_1) ? (
						<span class='team_1 winner ep-winners' />
					) : (
						<span class='team_1' />
					)}

					<span>{id_1}–{id_2}</span>

					{(winner_2) ? (
						<span class='team_2 winner ep-winners' />
					) : (
						<span class='team_2' />
					)}
				</span>
			</div>
		</div>
	)
});

const MatchResultMargin = forwardRef(({ user_answer, match, ...props }, ref) => {
	const [ result, setResult ] = useState(user_answer?.interval ?? props.intervals[0]);
	const [ winner, setWinner ] = useState(user_answer.winner);

	useImperativeHandle(ref, () => ({
		updateResults(results) {
			setWinner(results.winner);
			setResult(results.interval);
		},
	}), []);

	return (
		<div class="match_result is_margin">
			<div class="c">
				<span onClick={props.onClick}>
					{(winner == match.id_1) ? (
						<span class='team_1 winner ep-winners' />
					) : (
						<span class='team_1' />
					)}

					<span>{result.replace("-", "–")}</span>

					{(winner == match.id_2) ? (
						<span class='team_2 winner ep-winners' />
					) : (
						<span class='team_2' />
					)}
				</span>
			</div>
		</div>
	)
});

export const GuessScoreResumeWithPoints = ({ stage, teams, results, answers, ...props }) => {
	useCSS('comp/guess_score.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const type = stage?.guess_score_type ?? 'result';
	const is_bracket = (isNotEmpty(stage, 'guess_score_rounds')) ? true : false;

	const matches = getFilteredMatches(stage?.guess_score_matches ?? []);
	const is_builder = props?.is_builder ?? false;

	const step_interval = parseInt(stage?.guess_score_step_interval ?? '2');
	const max_interval = parseInt(stage?.guess_score_max_interval ?? '3');
	const points_result = parseInt(stage?.guess_score_points_result ?? '0');
	const points_outcome = parseInt(stage?.guess_score_points_outcome ?? '0');

	let display_points = props?.display_points ?? true;
	let display_resumee = props?.display_resumee ?? true;
	if(is_bracket && points_outcome == 0) {
		display_points = false;
		display_resumee = false;
	}

	let num_digits = (type == 'margin') ? 5 : parseInt(stage?.guess_score_num_digits ?? '1');
	if(type == 'margin') num_digits = 4;

	let intervals = [...Array(10).keys()];
	if(type == 'margin') intervals = getAvailableIntervals(step_interval, max_interval);

	const getGainedScoreInMatch = (match) => {
		const points = { points_result:0, points_winner:0, total:false };

		const has_result = results?.[match.id] ?? false;
		if(has_result === false) return points;

		points.total = 0;

		const result_1 = parseInt(results[match.id].id_1);
		const result_2 = parseInt(results[match.id].id_2);
		const result_interval = getIntervalFromResults(type, intervals, result_1, result_2);

		let result_winner;
		if(is_bracket === false) {
			if(result_1 > result_2) {
				result_winner = 'local';
			} else if(result_1 < result_2) {
				result_winner = 'visitor';
			} else {
				result_winner = 'draw';
			}
		} else {
			result_winner = (parseInt(results[match.id].winner) == 1) ? 'local' : 'visitor';
		}

		let answer_winner;
		if(is_bracket === false && type != 'margin') {
			const answer_result_1 = parseInt(answers[match.id]?.id_1 ?? '0');
			const answer_result_2 = parseInt(answers[match.id]?.id_2 ?? '0');

			if(answer_result_1 > answer_result_2) {
				answer_winner = 'local';
			} else if(answer_result_1 < answer_result_2) {
				answer_winner = 'visitor';
			} else {
				answer_winner = 'draw';
			}
		} else {
			if(answers[match.id].winner == match.id_1) {
				answer_winner = 'local';
			} else {
				answer_winner = 'visitor';
			}
		}

		if(is_bracket === false) {
			if(type != 'margin' && (result_1 == answers[match.id].id_1 && result_2 == answers[match.id].id_2)) {
				points.points_result = points_result;
				points.total = points.total + points_result;
			} else if(answers[match.id].interval == result_interval) {
				points.points_result = points_result;
				points.total = points.total + points_result;
			} else {
				points.points_result = 0;
			}
		}

		if(result_winner == answer_winner) {
			points.points_winner = points_outcome;
			points.total = points.total + points_outcome;
		} else {
			points.points_winner = 0;
		}

		return points;
	};

	const Component = (type == 'margin') ? MatchResultMargin : MatchResult;

	let total_points = 0;

	return (
		<div style={{ display:'flex', flexDirection:'column-reverse', gap:'1em' }}>
			<ul class="guess_results in_layer" style={{ '--num-digits':num_digits }}>
				{matches.map((match) => {
					const team_1 = getTeamInfoByID(teams, match.id_1);
					const team_2 = getTeamInfoByID(teams, match.id_2);

					const is_x2 = (ensureBoolean(match?.x2 ?? false)) ? true : false;
					const { points_result, points_winner, total } = getGainedScoreInMatch(match);
					const multiplier = (is_x2) ? 2 : 1;

					if(total !== false) total_points = total_points + multiplier * total;

					let match_status = 'ko';
					if(points_outcome > 0) {
						if(points_result > 0 && points_winner > 0) {
							match_status = 'ok';
						} else if(points_result > 0 || points_winner > 0) {
							match_status = 'semi_ok';
						}
					} else if(points_result > 0) {
						match_status = 'ok';
					}

					return (
						<li key={`match_${match.id}`}>
							<div class="c">
								<TeamInfo
									team={team_1}
									direction={-1}
								/>

								<Component
									user_answer={answers[match.id]}
									match={match}
									num_digits={num_digits}
									intervals={intervals}
									is_builder={is_builder}
								/>

								<TeamInfo
									team={team_2}
								/>
							</div>

							{(display_resumee && total !== false) && (
								<div class={`result_comparison is_${match_status}`}>
									{(is_bracket === false) && (
										<div>
											<FormattedMessage
												id="Real result: {result}{extra}"
												values={{
													result:<strong>{parseInt(results[match.id].id_1)}–{parseInt(results[match.id].id_2)}</strong>,
													extra:(type == 'margin') ? ` (${props.intl.formatMessage({ id:"difference of {val}" }, { val:Math.abs(parseInt(results[match.id].id_1) - parseInt(results[match.id].id_2))})})` : '',
												}}
											/>
										</div>
									)}

									{(display_points) && (
										<div class="points_table">
											{(is_bracket === false) && (
												<div>
													<strong class="l">{i18n("Points for exact result")}:</strong>
													<span class="s" />
													<span class="r">{points_result}</span>
												</div>
											)}

											{(points_outcome > 0) && (
												<div>
													<strong class="l">{i18n("Points for predicting the winning team")}:</strong>
													<span class="s" />
													<span class="r">{points_winner}</span>
												</div>
											)}

											{(is_x2 !== false) && (
												<div>
													<strong class="l">{i18n("x2 bonus")}:</strong>
													<span class="s" />
													<span class="r">{(total !== false) ? total : 0}</span>
												</div>
											)}

											<div class="total_row">
												<strong class="l total">{i18n("Total points")}:</strong>
												<span class="r">{multiplier * total}</span>
											</div>
										</div>
									)}
								</div>
							)}
						</li>
					)
				})}
			</ul>

			<div>{i18n("Score")}: <strong>{total_points} {i18n("points")}</strong>.</div>
		</div>
	)
};

const GuessScoreResume = ({ stage, ...props }) => {
	useCSS('comp/guess_score.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promotion = props?.promotion?.Promotion ?? {};
	const teams = localizeFields((promotion?.guess_score_teams ?? []), props.locale);

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const [ is_editing, setIsEditing ] = useState(false);

	let num_digits = parseInt(stage?.guess_score_num_digits ?? '1');
	const matches = getFilteredMatches(stage?.guess_score_matches ?? []);
	const type = stage?.guess_score_type ?? 'result';
	const step_interval = parseInt(stage?.guess_score_step_interval ?? '2');
	const max_interval = parseInt(stage?.guess_score_max_interval ?? '3');

	let intervals = [...Array(10).keys()];
	if(type == 'margin') intervals = getAvailableIntervals(step_interval, max_interval);

	const getRandomScores = () => {
		const results = {};
		matches.map((match) => {
			if(type == 'margin') {
				results[match.id] = {
					winner:match[`id_${Math.round(Math.random()) + 1}`],
					interval:intervals[Math.floor(Math.max(0, intervals.length * Math.random()))],
				};
				return;
			}

			results[match.id] = {
				id_1:Math.round(Math.pow(10, num_digits) * Math.random()) - 1,
				id_2:Math.round(Math.pow(10, num_digits) * Math.random()) - 1,
			};
		});
		return results;
	};

	const refs = useRef({});
	const user_answer = useRef((is_builder && !is_test) ? getRandomScores() : (props?.user_answer ?? {}));

	const main_description_placeholder = `<h1>${i18n("Your results")}</h1><p>${i18n("Click on the scores to change them.")}</p>`;
	const resume_message = (isNotEmpty(stage, 'guess_resume_message')) ? stage.guess_resume_message : main_description_placeholder;

	if(type == 'margin') num_digits = 4;

	const onUpdateMatch = (_matches) => {
		if(is_builder && !is_test) return;

		refs.current[`match_result_${is_editing}`].updateResults(_matches[is_editing]);
		user_answer.current = _matches;

		setIsEditing(false);
	};

	const updateMatches = () => {
		if(is_builder && !is_test) {
			setIsEditing(false);
			return;
		}

		refs.current['canvas'].updateMatches();
	};

	const confirmResults = (e, cb) => {
		props.onEnded(true, { guess_score:user_answer.current });
		if(cb != undefined) cb();
	};

	const Component = (type == 'margin') ? MatchResultMargin : MatchResult;

	return (
		<div>
			{(resume_message != " ") && (
				<LeveledHTMLText>{resume_message}</LeveledHTMLText>
			)}

			<ul class="guess_results" style={{ '--num-digits':num_digits }}>
				{matches.map((match) => {
					const team_1 = getTeamInfoByID(teams, match.id_1);
					const team_2 = getTeamInfoByID(teams, match.id_2);

					return (
						<li key={`match_${match.id}`}>
							<div class="c">
								<TeamInfo
									team={team_1}
									direction={-1}
								/>

								<Component
									ref={(node) => { refs.current[`match_result_${match.id}`] = node; }}
									user_answer={user_answer.current[match.id]}
									match={match}
									num_digits={num_digits}
									intervals={intervals}
									onClick={() => setIsEditing(match.id)}
									is_builder={is_builder}
									is_test={is_test}
								/>

								<TeamInfo
									team={team_2}
								/>
							</div>
						</li>
					)
				})}
			</ul>

			<ButtonCollection
				_class={{ other:'flex_button gui_big_button' }}
			>
				<SaveButton
					_fullclass=""
					onClick={confirmResults}
				>Confirm</SaveButton>
			</ButtonCollection>

			<p class="gui_not_in_top_small gui_inline_block">
				<span class="knk-edit gui_small_icon" />{i18n("You can still edit your predictions if needed.")}
			</p>

			{(is_editing !== false) && (
				<FloatingLayerPublic
					pm={props.promotion}
					is_builder={is_builder}
					enable_behavior={true}
					backButton={() => setIsEditing(false)}
				>
					<div class="gui_game_wrapper">
						<GuessScoreCanvas
							ref={(node) => { refs.current['canvas'] = node; }}
							{...props}
							stage={stage}
							teams={teams}
							is_editing={true}
							current_match={is_editing}
							user_answer={user_answer.current}
							onUpdate={onUpdateMatch}
						/>
					</div>

					<ButtonCollection
						_class={{ other:'flex_button gui_big_button gui_not_in_top_big' }}
					>
						<ActionButton
							_fullclass=""
							onClick={updateMatches}
						>Save changes</ActionButton>
					</ButtonCollection>
				</FloatingLayerPublic>
			)}
		</div>
	);
};

export default injectIntl(GuessScoreResume);